/**
 * The language of what the chat writes (a conversation, a quiz), the same in the Studio and in reader-web (Stéphane,
 * 19/10/2026): asked at the first chat or quiz on a book in another language than the interface (the book's or the
 * interface's, remembered for the book or not); the two flags in the area of the question of a new conversation or quiz
 * start on that choice and change it for that one only, which then keeps its language. The answers of a pipeline that
 * does not write in that language (V1) are translated by the server (POST /translate/many, a Mistral model).
 */

export type ContentMode = "book" | "interface";

export const baseLanguage = (language: string | null | undefined) => language?.trim().toLowerCase().split(/[-_]/)[0] ?? "";

/** The flag shown for a language: the country most read in it; none for a language without an obvious one. */
const FLAGS: Record<string, string> = {
  fr: "🇫🇷", en: "🇬🇧", es: "🇪🇸", de: "🇩🇪", it: "🇮🇹", pt: "🇵🇹", nl: "🇳🇱", ar: "🇸🇦", zh: "🇨🇳", ja: "🇯🇵",
  ru: "🇷🇺", uk: "🇺🇦", pl: "🇵🇱", tr: "🇹🇷", ko: "🇰🇷", he: "🇮🇱", el: "🇬🇷", fa: "🇮🇷", ur: "🇵🇰", hi: "🇮🇳",
  th: "🇹🇭", bg: "🇧🇬", sr: "🇷🇸", ro: "🇷🇴", sv: "🇸🇪", da: "🇩🇰", no: "🇳🇴", nb: "🇳🇴", fi: "🇫🇮", cs: "🇨🇿",
  hu: "🇭🇺", vi: "🇻🇳", id: "🇮🇩",
};

export function flagOf(language: string): string | undefined {
  return FLAGS[baseLanguage(language)];
}

/** The toggle is offered when the book is known to be in another language than the interface. */
export function offersToggle(bookLanguage: string | null | undefined, interfaceLanguage: string): boolean {
  const book = baseLanguage(bookLanguage);
  return book.length >= 2 && book !== baseLanguage(interfaceLanguage);
}

const LATIN = /\p{Script=Latin}/gu;
const FRENCH_WORDS = new Set(["le", "la", "les", "des", "une", "est", "que", "qui", "dans", "pour", "pas", "et", "du", "au", "avec", "sur", "ne", "ce", "cette", "il", "elle", "sont", "plus", "mais", "ou", "par"]);
const ENGLISH_WORDS = new Set(["the", "and", "of", "to", "is", "in", "that", "with", "for", "are", "this", "was", "it", "as", "on", "by", "not", "but", "his", "her", "which"]);

/** The writing of the languages that have their own: a text mostly in it is in that language. */
const SCRIPTS: Record<string, RegExp> = {
  ar: /\p{Script=Arabic}/gu, fa: /\p{Script=Arabic}/gu, ur: /\p{Script=Arabic}/gu, he: /\p{Script=Hebrew}/gu,
  yi: /\p{Script=Hebrew}/gu, ru: /\p{Script=Cyrillic}/gu, uk: /\p{Script=Cyrillic}/gu, bg: /\p{Script=Cyrillic}/gu,
  sr: /\p{Script=Cyrillic}/gu, el: /\p{Script=Greek}/gu, zh: /\p{Script=Han}/gu,
  ja: /[\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}]/gu, ko: /\p{Script=Hangul}/gu,
  hi: /\p{Script=Devanagari}/gu, th: /\p{Script=Thai}/gu,
};

/**
 * Whether a text is plainly already in a language, so it is not sent to be translated: mostly in the writing of a
 * language that has its own; for French and English, mostly Latin letters and more of its common words than of the
 * other's. A text in another language in Latin letters is translated (the server leaves it as it is if it is already).
 */
export function alreadyIn(text: string, language: string): boolean {
  const letters = text.match(/\p{L}/gu)?.length ?? 0;
  if (!letters) return true;
  const script = SCRIPTS[baseLanguage(language)];
  if (script) return (text.match(script)?.length ?? 0) * 2 >= letters;
  if ((text.match(LATIN)?.length ?? 0) * 2 < letters) return false;
  let french = 0;
  let english = 0;
  for (const word of text.toLowerCase().match(/\p{L}+/gu) ?? []) {
    if (FRENCH_WORDS.has(word)) french++;
    if (ENGLISH_WORDS.has(word)) english++;
  }
  const code = baseLanguage(language);
  const words = text.match(/\p{L}+/gu)?.length ?? 0;
  // A short text (a choice of the quiz: a name, a year) has no common word: it is translated when it is not Latin only.
  if (code === "fr") return french > english && (french >= 2 || words <= 3);
  if (code === "en") return english > french && (english >= 2 || words <= 3);
  return false;
}

/**
 * The choice of the reader, per book. Asked at the first chat or quiz on a book in another language (Stéphane,
 * 19/10/2026): kept on this device when the reader asks to remember it, else for the visit only (asked again next time).
 */
const STORAGE_KEY = "msb-reader:content-language";

export type ContentChoice = { mode: ContentMode; remembered: boolean };

function stored(storage: () => Storage): Record<string, unknown> {
  try {
    return JSON.parse(storage().getItem(STORAGE_KEY) ?? "{}") as Record<string, unknown>;
  } catch {
    return {};
  }
}

function store(storage: () => Storage, book: string, mode: ContentMode | null) {
  try {
    const saved = stored(storage);
    if (mode) saved[book] = mode;
    else delete saved[book];
    storage().setItem(STORAGE_KEY, JSON.stringify(saved));
  } catch {
    // Private browsing: the choice lasts as long as the page.
  }
}

const isMode = (value: unknown): value is ContentMode => value === "book" || value === "interface";

/** The choice made for a book: null when the reader was not asked yet (or did not keep it after the last visit). */
export function loadChoice(book: string): ContentChoice | null {
  const remembered = stored(() => localStorage)[book];
  if (isMode(remembered)) return { mode: remembered, remembered: true };
  const visit = stored(() => sessionStorage)[book];
  return isMode(visit) ? { mode: visit, remembered: false } : null;
}

export function saveChoice(book: string, choice: ContentChoice) {
  store(() => localStorage, book, choice.remembered ? choice.mode : null);
  store(() => sessionStorage, book, choice.remembered ? null : choice.mode);
}

/** A part of a short text of the quiz (a question, a choice), with its inline Markdown. */
export type InlinePart = { text: string; strong?: boolean; em?: boolean; code?: boolean };

const INLINE = /(\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\s][^*\n]*\*|_[^_\s][^_\n]*_|`[^`\n]+`)/;

/**
 * The inline Markdown of a short text of the quiz, as parts to show (bold, italic, code): the questions are short,
 * and their translation may add some. The marks of a title or a list at the start of a line are dropped.
 */
export function inlineMarkdown(text: string): InlinePart[] {
  const plain = text.replace(/^\s{0,3}(?:#{1,6}\s+|[-*+]\s+|>\s?)/gm, "");
  return plain
    .split(INLINE)
    .filter(Boolean)
    .map((part) => {
      if (/^(\*\*|__).+\1$/.test(part)) return { text: part.slice(2, -2), strong: true };
      if (/^`.+`$/.test(part)) return { text: part.slice(1, -1), code: true };
      if (/^([*_]).+\1$/.test(part) && part.length > 2) return { text: part.slice(1, -1), em: true };
      return { text: part };
    });
}

/** What the server takes in one call (see modules/translation/routes.py). */
const MAX_TEXTS = 40;
const MAX_TEXT = 5000;
const MAX_TEXTS_LENGTH = 40000;

/** Splits texts into the calls the server accepts, each text cut to the longest it takes. */
export function batches(texts: string[]): string[][] {
  const out: string[][] = [];
  let current: string[] = [];
  let length = 0;
  for (const whole of texts) {
    const text = whole.slice(0, MAX_TEXT);
    if (current.length && (current.length >= MAX_TEXTS || length + text.length > MAX_TEXTS_LENGTH)) {
      out.push(current);
      current = [];
      length = 0;
    }
    current.push(text);
    length += text.length;
  }
  if (current.length) out.push(current);
  return out;
}

export type TranslateMany = (texts: string[], source: string, target: string) => Promise<string[] | null>;

/** Translates texts with the server of the Studio or of the reader; null when it does not answer. */
export function serverTranslator(url: string, headers: Record<string, string>): TranslateMany {
  return async (texts, source, target) => {
    const out: string[] = [];
    for (const batch of batches(texts)) {
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ texts: batch, source, target }),
        });
        if (!response.ok) return null;
        const { translations } = (await response.json()) as { translations?: unknown };
        if (!Array.isArray(translations) || translations.length !== batch.length) return null;
        out.push(...translations.map((item, index) => (typeof item === "string" && item.trim() ? item : batch[index]!)));
      } catch {
        return null;
      }
    }
    return out;
  };
}

/** Translations already received, by target language and text: going back and forth between the flags costs nothing. */
const cache = new Map<string, string>();
const cacheKey = (target: string, text: string) => `${target}\u0000${text}`;

export function cached(text: string, target: string): string | undefined {
  return cache.get(cacheKey(target, text));
}

/** Translates the texts not known yet (each once, even when asked twice at the same time); false when it failed. */
const inFlight = new Map<string, Promise<boolean>>();

export async function translateMissing(texts: string[], source: string, target: string, translate: TranslateMany): Promise<boolean> {
  const missing = [...new Set(texts)].filter((text) => !cache.has(cacheKey(target, text)) && !inFlight.has(cacheKey(target, text)));
  const waiting = [...new Set(texts)].map((text) => inFlight.get(cacheKey(target, text))).filter((p): p is Promise<boolean> => !!p);
  let ok = true;
  if (missing.length) {
    const call = translate(missing, source, target).then((translations) => {
      if (!translations) return false;
      missing.forEach((text, index) => cache.set(cacheKey(target, text), translations[index] ?? text));
      return true;
    });
    for (const text of missing) inFlight.set(cacheKey(target, text), call);
    try {
      ok = await call;
    } finally {
      for (const text of missing) inFlight.delete(cacheKey(target, text));
    }
  }
  const others = await Promise.all(waiting);
  return ok && others.every(Boolean);
}
